import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { parseAsString, useQueryState } from 'nuqs';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCheckValidForgotPasswordToken, useResetForgotPassword } from '../../query/auth';

import InputField from '../fields/input-field';
import { Button } from '../ui/button';
import { Form } from '../ui/form';

const formSchema = z
  .object({
    password: z
      .string({
        required_error: '비밀번호를 입력해주세요.',
      })
      .min(6, '비밀번호는 6자 이상이어야 합니다.'),
    rePassword: z
      .string({
        required_error: '비밀번호를 재입력해주세요.',
      })
      .min(6, '비밀번호 재입력은 필수입니다.'),
  })
  .refine(data => data.password === data.rePassword, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['rePassword'], // 이 에러 메시지가 `rePassword` 필드에 표시됩니다.
  });

type FormData = z.infer<typeof formSchema>;

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      rePassword: '',
    },
    reValidateMode: 'onChange',
  });
  const [token] = useQueryState('token', parseAsString.withDefault(''));

  const { mutate: resetForgotPassword, isPending: isResetForgotPassword } = useResetForgotPassword();
  const { isError } = useCheckValidForgotPasswordToken(token);

  useEffect(() => {
    if (isError) {
      navigate('/sign-in', { replace: true });
    }
  }, [isError]);
  const onSubmit = (data: FormData) => {
    resetForgotPassword(
      { ...data, token },
      {
        onSuccess: () => {
          toast.success('비밀번호 수정했습니다.');
          navigate('/sign-in', { replace: true });
        },
        onError: () => {
          toast.error('비밀번호 수정하는데 문제가 발생했습니다.');
        },
      },
    );
  };
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <InputField
          control={form.control}
          name="password"
          placeholder="비밀번호를 입력해주세요."
          label="비밀번호"
          type="password"
          autoComplete="current-password"
        />
        <InputField
          control={form.control}
          name="rePassword"
          placeholder="비밀번호를 다시 입력해주세요."
          label="비밀번호 재입력"
          type="password"
        />

        <div className="flex w-full items-center justify-center gap-4">
          <Button type="submit" disabled={isResetForgotPassword}>비밀번호 설정</Button>
        </div>
      </form>
    </Form>
  );
};

export default ResetPasswordForm;
